import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Save, Trash2, Building2, Building } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import EmptyState from '@/components/EmptyState';
import useBulkSelect, { bulkDelete } from '@/hooks/useBulkSelect';
import BulkDeleteBar from '@/components/BulkDeleteBar';

export default function CompanyRegistryPage() {
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState('');
  const [branchForm, setBranchForm] = useState({ name: '', address: '', city: '', postalCode: '' });

  // Cache-busting param — brání prohlížeči/CDN vrátit zastaralý seznam (GET /companies).
  const load = () => api.get('/companies', { params: { _t: Date.now() } })
    .then(r => { setCompanies(r.data); return r.data; })
    .catch(() => []);
  useEffect(() => { load(); }, []);

  const selectCompany = (c) => { setSelected(c.id); setForm({ ...c }); };

  const create = async () => {
    try {
      const res = await api.post('/companies', { name: 'Nová společnost' });
      // Optimistický zápis do seznamu hned + jistící refetch (await, ať je pořadí správné).
      setCompanies(prev => [...prev, res.data]);
      selectCompany(res.data);
      await load();
      toast.success('Společnost vytvořena');
    } catch { toast.error('Chyba'); }
  };

  const save = async () => {
    if (!selected) return;
    try {
      const res = await api.put(`/companies/${selected}`, form);
      // Optimisticky přepiš položku v seznamu, ať se změna projeví okamžitě.
      setCompanies(prev => prev.map(c => (c.id === selected ? { ...c, ...res.data } : c)));
      await load();
      toast.success('Uloženo');
    } catch { toast.error('Chyba při ukládání'); }
  };

  const remove = async () => {
    if (!selected || !window.confirm('Opravdu smazat?')) return;
    try {
      await api.delete(`/companies/${selected}`);
      setCompanies(prev => prev.filter(c => c.id !== selected));
      setSelected(null);
      await load();
      toast.success('Smazáno');
    } catch { toast.error('Chyba při mazání'); }
  };

  const addBranch = () => {
    const branches = [...(form.branches || []), { ...branchForm }];
    setForm({ ...form, branches });
    setBranchForm({ name: '', address: '', city: '', postalCode: '' });
  };

  const removeBranch = (idx) => {
    const branches = form.branches.filter((_, i) => i !== idx);
    setForm({ ...form, branches });
  };

  const filtered = companies.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()));

  const sel = useBulkSelect(filtered.map(c => c.id));

  const bulkRemove = async () => {
    const ids = sel.selectedIds;
    const { ok, failed } = await bulkDelete(ids, id => api.delete(`/companies/${id}`));
    if (selected && ids.includes(selected)) { setSelected(null); setForm({}); }
    sel.clear();
    await load();
    if (failed) toast.warning(`Smazáno ${ok}, ${failed} selhalo (možná mají vazby)`);
    else toast.success(`Smazáno ${ok}`);
  };

  return (
    <div data-testid="company-registry-page" className="flex gap-4 h-[calc(100vh-7rem)]">
      {/* Left: List */}
      <div className="ct-panel w-96 flex-shrink-0 flex flex-col">
        <div className="ct-panel-header flex items-center justify-between">
          <span>Společnosti</span>
          <button data-testid="add-company-btn" onClick={create} className="p-1 rounded" style={{ color: 'var(--ct-accent)' }}>
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="p-2 space-y-2">
          <Input data-testid="company-search" placeholder="Hledat..." value={search} onChange={e => setSearch(e.target.value)} className="h-8 text-sm" />
          {filtered.length > 0 && (
            <label className="flex items-center gap-2 px-1 text-xs cursor-pointer" style={{ color: 'var(--ct-text-secondary)' }}>
              <Checkbox checked={sel.allSelected} onCheckedChange={sel.toggleAll} aria-label="Vybrat vše" />
              Vybrat vše
            </label>
          )}
          <BulkDeleteBar count={sel.count} onDelete={bulkRemove} onClear={sel.clear} entityLabel="společností" />
        </div>
        <ScrollArea className="flex-1">
          {filtered.map((c, i) => (
            <div
              key={c.id}
              className={`ct-table-row flex items-center gap-2 px-3 py-2 text-sm ${selected === c.id ? 'active' : ''}`}
            >
              <Checkbox
                checked={sel.isSelected(c.id)}
                onCheckedChange={() => sel.toggle(c.id)}
                aria-label={`Vybrat ${c.name}`}
              />
              <button
                data-testid={`company-item-${c.id}`}
                onClick={() => selectCompany(c)}
                className="flex-1 text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{i + 1}</span>
                  <div>
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{c.city}</div>
                  </div>
                </div>
              </button>
            </div>
          ))}
        </ScrollArea>
      </div>

      {/* Right: Detail */}
      {selected ? (
        <div className="flex-1 flex flex-col gap-4 overflow-auto">
          <div className="ct-panel">
            <div className="ct-panel-header flex items-center justify-between">
              <span>Detail společnosti</span>
              <button data-testid="delete-company-btn" onClick={remove} className="p-1 rounded" style={{ color: 'var(--ct-error)' }}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-4">
              <div className="ct-form-field col-span-2">
                <label className="ct-form-label">Název společnosti</label>
                <Input data-testid="company-name" value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className="h-9 text-sm" />
              </div>
              <div className="ct-form-field col-span-2">
                <label className="ct-form-label">IČO</label>
                <Input
                  data-testid="company-ico"
                  value={form.ico || ''}
                  onChange={e => setForm({ ...form, ico: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                  placeholder="8 číslic"
                  inputMode="numeric"
                  className="h-9 text-sm w-40"
                />
                {form.ico && form.ico.length > 0 && form.ico.length !== 8 && (
                  <p className="text-xs mt-1" style={{ color: 'var(--ct-error)' }}>
                    IČO musí mít 8 číslic.
                  </p>
                )}
              </div>
              <div className="ct-form-field col-span-2">
                <label className="ct-form-label">Adresa</label>
                <Input value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} className="h-9 text-sm" />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">Město</label>
                <Input value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} className="h-9 text-sm" />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">PSČ</label>
                <Input value={form.postalCode || ''} onChange={e => setForm({ ...form, postalCode: e.target.value })} className="h-9 text-sm" />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">Země</label>
                <Input value={form.country || ''} onChange={e => setForm({ ...form, country: e.target.value })} className="h-9 text-sm" />
              </div>
              <div className="ct-form-field col-span-2">
                <label className="ct-form-label">Role firmy</label>
                <div className="flex gap-4 mt-1">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      data-testid="company-flag-manufacturer"
                      checked={form.isManufacturer ?? false}
                      onCheckedChange={v => setForm({ ...form, isManufacturer: !!v })}
                    />
                    <span>Výrobce</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      data-testid="company-flag-supplier"
                      checked={form.isSupplier ?? false}
                      onCheckedChange={v => setForm({ ...form, isSupplier: !!v })}
                    />
                    <span>Dodavatel</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      data-testid="company-flag-operator"
                      checked={form.isOperator ?? false}
                      onCheckedChange={v => setForm({ ...form, isOperator: !!v })}
                    />
                    <span>Provozovatel</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      data-testid="company-flag-customer"
                      checked={form.isCustomer ?? false}
                      onCheckedChange={v => setForm({ ...form, isCustomer: !!v })}
                    />
                    <span>Zákazník</span>
                  </label>
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--ct-text-secondary)' }}>
                  Určuje, v kterých výběrech se firma nabízí — Výrobce/Dodavatel/Provozovatel
                  v Evidenci přístrojů, Zákazník u objednávek a v portálu.
                </p>
              </div>
            </div>
            <div className="px-4 pb-4">
              <Button data-testid="save-company-btn" onClick={save} className="ct-btn-primary gap-1">
                <Save className="w-4 h-4" /> Uložit
              </Button>
            </div>
          </div>

          {/* Branches */}
          <div className="ct-panel">
            <div className="ct-panel-header flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Pobočky společnosti
            </div>
            <div className="p-4">
              {(form.branches || []).map((b, idx) => (
                <div key={idx} className="flex items-center gap-2 mb-2 p-2 rounded" style={{ background: 'var(--ct-bg-secondary)', border: '1px solid var(--ct-border)', color: 'var(--ct-text)' }}>
                  <div className="flex-1 grid grid-cols-4 gap-2 text-sm">
                    <span>{b.name}</span>
                    <span>{b.address}</span>
                    <span>{b.city}</span>
                    <span>{b.postalCode}</span>
                  </div>
                  <button onClick={() => removeBranch(idx)} className="p-1" style={{ color: 'var(--ct-error)' }}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <div className="grid grid-cols-4 gap-2 mt-3">
                <Input placeholder="Název" value={branchForm.name} onChange={e => setBranchForm({ ...branchForm, name: e.target.value })} className="h-8 text-sm" />
                <Input placeholder="Adresa" value={branchForm.address} onChange={e => setBranchForm({ ...branchForm, address: e.target.value })} className="h-8 text-sm" />
                <Input placeholder="Město" value={branchForm.city} onChange={e => setBranchForm({ ...branchForm, city: e.target.value })} className="h-8 text-sm" />
                <div className="flex gap-1">
                  <Input placeholder="PSČ" value={branchForm.postalCode} onChange={e => setBranchForm({ ...branchForm, postalCode: e.target.value })} className="h-8 text-sm" />
                  <Button onClick={addBranch} size="sm" className="ct-btn-primary h-8 px-2">
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={Building}
            title="Vyberte společnost"
            description="Klikněte na společnost v seznamu vlevo pro úpravu kontaktů a rolí."
          />
        </div>
      )}
    </div>
  );
}
